@file:OptIn(ExperimentalTime::class)

package com.macrofocus.eflux.ui

import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.docking.js.CPFiller
import com.macrofocus.docking.js.JsDockingPanel
import com.macrofocus.eflux.*
import com.macrofocus.eflux.components.PrimaryInfo
import com.macrofocus.eflux.components.SecondaryInfo
import com.macrofocus.eflux.components.Sidebar
import com.macrofocus.eflux.components.blurBackground
import com.macrofocus.eflux.data.quest.loadLogicalBranchDataFrames
import com.macrofocus.eflux.db.branchDataFrame
import com.macrofocus.eflux.diagram.DiagramType
import com.macrofocus.eflux.lod.createChordDiagram
import com.macrofocus.eflux.network.physical.Branch
import com.macrofocus.eflux.network.physical.Bus
import com.macrofocus.eflux.ui.bar.SouthFabBar
import com.macrofocus.eflux.ui.bar.WestFabBar
import com.macrofocus.eflux.ui.mui.EFluxSearch
import com.macrofocus.eflux.ui.mui.themes.Themes
import com.macrofocus.eflux.ui.mui.themes.useSetTheme
import com.macrofocus.eflux.ui.perspective.*
import com.macrofocus.eflux.ui.table.TableView
import emotion.react.css
import js.objects.unsafeJso
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import mui.icons.material.Close
import mui.icons.material.TerrainTwoTone
import mui.material.*
import mui.material.styles.Theme
import mui.material.styles.useTheme
import mui.system.SxProps
import mui.x.DataGrid
import mui.x.GridColDef
import org.mkui.component.CPComponent
import org.mkui.component.ReactComponent
import org.mkui.react.useProperty
import org.mkui.react.useSelection
import org.molap.dataframe.DataFrame
import org.molap.dataframe.rowmajor.RowMajorDataFrame
import org.molap.json.readJson
import react.*
import react.dom.html.ReactHTML.div
import tanstack.react.router.useSearch
import tanstack.react.router.useNavigate
import tanstack.router.core.NavigateOptions
import web.cssom.*
import web.html.HtmlSource
import web.url.URLSearchParams
import kotlin.time.ExperimentalTime

external interface RowData {
    var id: Any?
}

enum class EFluxWindows {
    Diagram,
    Details,
    Table,
}

external interface EFluxProps : Props {
    var dataLoader: DataLoader
    var diagramLoaders: Array<ViewLoader>
    var diagramType: DiagramType
    var paletteType: PaletteType
    var showTable: MutableProperty<Boolean>
}

val EFluxApp = FC<EFluxProps> { props ->
    val (showTable, ) = useProperty(props.showTable)
    val (expertMode, ) = useProperty(props.dataLoader.exportMode)

    val search = useSearch() // This automatically returns an EFluxSearch object!
    val navigate = useNavigate()
    val useSetTheme = useSetTheme()

    val (live, _) = useProperty(props.dataLoader.configuration.live)
    val (masterDateTime, _) = useProperty(props.dataLoader.configuration.masterDateTime)

    val searchDate = search.get("date")

    // Keep the URL's date search parameter in sync with the selected date
    useEffect(live, masterDateTime, searchDate) {
        if(!live) {
            if(searchDate == null || searchDate != masterDateTime.toRawString()) {
                navigate {
//                this.from = "/index"
                    this.asDynamic().search = unsafeJso<EFluxSearch> { this.date = masterDateTime.toRawString() }
                }
            }
        } else {
            if(searchDate != null) {
                navigate {
//                this.from = "/index"
//                this.search = unsafeJso<EFluxSearch> { this.date = null }
//                this.search = null
                    this.asDynamic().search = unsafeJso<EFluxSearch> { this.date = undefined.unsafeCast<String>() }
                }
            }
        }
    }

    // AutoTheme
    useEffect(masterDateTime) {
        val now = masterDateTime.toRawString().toDateTimeTz().datetime
        val (sunrise, sunset) = sunriseSunset(now.toLocalDateTime(TimeZone.of("Europe/Zurich")).date)
        if(sunrise != null && sunset != null) {
//            println("Zurich on ${now.toLocalDateTime(TimeZone.of("Europe/Zurich"))} — Sunrise: ${sunrise.toLocalDateTime(TimeZone.of("Europe/Zurich"))}, Sunset: ${sunset.toLocalDateTime(TimeZone.of("Europe/Zurich"))}")
            if (now > sunrise && now < sunset) {
                useSetTheme(Themes.LightDiviso)
            } else {
                useSetTheme(Themes.DarkDiviso)
            }
        }
    }

    println("Switching to " + props.diagramType + ", " + showTable)

    val docking = useMemo {
        //            val comp0 = CPFiller ( backgroundColor = NamedColor.aquamarine )
        val comp0 = object: CPComponent {
            override val nativeComponent: FC<*> = FC<Props> { p ->
                div {
                    css {
                        position = Position.relative
                        top = 0.px
                        bottom = 0.px
                        left = 0.px
                        right = 0.px
                        pointerEvents = None.none
                    }
                }
            }
        }
        val dataGrid = ReactComponent(FC<Props> {
            val theme = useTheme<Theme>()

            val customBackground: (SxProps<Theme>) -> Unit = { it ->
                it.blurBackground(theme)
            }

            var lines by useState<DataFrame<*,String,*>>()

            val (getShiftedDateTime, _) = useProperty(props.dataLoader.masterDateTime)
            val shiftedDateTime = getShiftedDateTime.toRawString()

            useEffect(shiftedDateTime) {
                window.fetch("data/substations-eflux.json?datetime=$shiftedDateTime").then {
                    it.text().then {
                        val dataFrame = DataFrame.readJson(it)
                        lines = dataFrame
                    }
                }
            }

            val rows = if(lines != null) Array<RowData>(lines!!.rowCount) { i ->
                unsafeJso<RowData> {
                    id = lines!!.rowIndex.getKey(i)
                }
            } else emptyArray()

            val columns = if(lines != null) Array<GridColDef>(lines!!.columnCount) { i ->
                unsafeJso<GridColDef> {
                    field = lines!!.columnIndex.getKey(i)
                    headerName = lines!!.getColumnName(lines!!.columnIndex.getKey(i))
//                    width = 200
                    valueGetter = { value, row, column, apiRef ->
                        if (row != null) {
                            val v = lines?.getValueAt(row.id, lines!!.columnIndex.getKey(i))
                            v
                        } else {
                            "null"
                        }
                    }
                }
            } else emptyArray()

            div {
                css {
                    position = Position.relative
                    display = Display.flex
                    flexDirection = FlexDirection.column
                    minHeight = 0.px
                    width = 100.pct
                    height = 100.pct
                    maxHeight = 100.pct
//                    top = 0.px
//                    bottom = 0.px
//                    left = 0.px
//                    right = 0.px
//                    maxHeight = 100.vh

                    customBackground(this)
                }

                DataGrid {
                    asDynamic().density = "compact"
                    this.rows = rows
                    this.columns = columns
//                pageSizeOptions = arrayOf(5)
                }
            }
        })
        val search = SearchPerspective(props.dataLoader)
        val clock = ClockPerspective(props.dataLoader)
        val layers = LayersPerspective(props.dataLoader)
        val parameters = ParametersPerspective(props.dataLoader)
        val settings = SettingsPerspective(props.dataLoader)
        val buses = TablePerspective(props.dataLoader, SimpleProperty(TableType.BUS), SimpleProperty(false))
        val branches = TablePerspective(props.dataLoader, SimpleProperty(TableType.BRANCH), SimpleProperty(false))
        val distributions = DistributionsPerspective(props.dataLoader)

        val docking: JsDockingPanel = JsDockingPanel(true)
        docking.horizontalFirstDividerSize = 320.0
        docking.horizontalLastDividerSize = 300.0 * 2.0 // Two columns layout
        docking.verticalFirstDividerSize = 0.0
        docking.verticalLastDividerSize = 300.0
        //        ImageIcon icon = new ImageIcon(getClass().getResource("macrofocus.png"));
        val icon: String? = null
        docking.innerComponent = docking.createDecoratedDockable(comp0, "Zero", icon).component

        val searchDockable = docking.createDecoratedDockable(search, "Search", icon)
        docking.westDockingBar.attach(searchDockable)
        docking.westDockingBar.setActive(searchDockable, true)

        if(expertMode) {
            val filter = CPFiller(backgroundColor = NamedColor.magenta)
            docking.westDockingBar.attach(docking.createDecoratedDockable(filter, "Filter", icon))
        }

        val clockDockable = docking.createDecoratedDockable(clock, "Clock", icon)
        docking.westDockingBar.attach(clockDockable)
        docking.westDockingBar.attach(docking.createDecoratedDockable(layers, "Layers", icon))
        docking.westDockingBar.attach(docking.createDecoratedDockable(parameters, "Parameters", icon))
        docking.westDockingBar.attach(docking.createDecoratedDockable(settings, "Settings", icon))
        if(expertMode) {
            val substations = DataGridPerspective(props.dataLoader, "data/substations-eflux.json")
            val lines = DataGridPerspective(props.dataLoader, "data/lines-eflux.json")
            docking.westDockingBar.attach(docking.createDecoratedDockable(substations, "Substation", icon))
            docking.westDockingBar.attach(docking.createDecoratedDockable(lines, "Lines", icon))
        }
        val risks = DataGridPerspective(props.dataLoader, "db/risks")
        docking.westDockingBar.attach(docking.createDecoratedDockable(risks, "Risks", icon))
//        docking.westDockingBar.attach(docking.createDecoratedDockable(comp4, "Four", icon))

        val primaryInfo = ReactComponent(PrimaryInfo)

        val secondaryInfo = ReactComponent(SecondaryInfo)
        val infoDockable = docking.createDecoratedDockable(CPTwoColumnsInfo(props.dataLoader.controller.secondary, primaryInfo, secondaryInfo), "Info", icon)
        docking.eastDockingBar.attach(infoDockable)
        if(expertMode) {
            val filter = CPFiller(backgroundColor = NamedColor.magenta)
            docking.eastDockingBar.attach(docking.createDecoratedDockable(CPTwoColumnsComponent(null, filter), "Chat", icon))
            docking.eastDockingBar.attach(docking.createDecoratedDockable(CPTwoColumnsComponent(null, filter), "War room", icon))
            docking.eastDockingBar.attach(docking.createDecoratedDockable(CPTwoColumnsComponent(null, filter), "Links", icon))
        }
        docking.eastDockingBar.attach(
            docking.createDecoratedDockable(
                CPTwoColumnsComponent(null, AboutPerspective(props.dataLoader)),
                "About",
                icon
            )
        )
//            docking.northDockingBar.attach(docking.createDecoratedDockable(comp9, "Nine", icon))
//            docking.northDockingBar.attach(docking.createDecoratedDockable(comp10, "Ten", icon))
        docking.eastDockingBar.setActive(infoDockable, true)

        if(expertMode) {
            val table = TablePerspective(props.dataLoader)
            val tableDockable = docking.createDecoratedDockable(table, "Table", icon)
            docking.southDockingBar.attach(tableDockable)
        }

        val busesDockable = docking.createDecoratedDockable(buses, "Substations", icon)
        val branchesDockable = docking.createDecoratedDockable(branches, "Lines", icon)
        docking.southDockingBar.attach(busesDockable)
        docking.southDockingBar.attach(branchesDockable)

        if(expertMode) {
            val parallelCoordinates = ParallelCoordinatesPerspective(props.dataLoader)
            val scatterPlot = ScatterPlotPerspective(props.dataLoader)
            val tableLens = TableLensPerspective(props.dataLoader)
            val timeSeries = TimeSeriesPerspective(props.dataLoader)
            docking.southDockingBar.attach(
                docking.createDecoratedDockable(
                    parallelCoordinates,
                    "Parallel coordinates",
                    icon
                )
            )
            docking.southDockingBar.attach(docking.createDecoratedDockable(distributions, "Distributions", icon))
            docking.southDockingBar.attach(docking.createDecoratedDockable(scatterPlot, "Scatter plot", icon))
            docking.southDockingBar.attach(docking.createDecoratedDockable(tableLens, "Table lens", icon))
            docking.southDockingBar.attach(docking.createDecoratedDockable(timeSeries, "Time series", icon))
            val comp4 = CPFiller ( backgroundColor = NamedColor.pink )
            docking.southDockingBar.attach(docking.createDecoratedDockable(comp4, "Heatmap", icon))
        }
        docking.southDockingBar.setActive(docking.southDockingBar.dockables!!.first()!!, false)

        docking
    }

    val (pointSelection, _) = useSelection(props.dataLoader.configuration.logicalBusSelection)
    val (pathSelection, _) = useSelection(props.dataLoader.configuration.logicalBranchSelection)

    val (getMasterDateTime, _) = useProperty(props.dataLoader.configuration.maxDateTime)
    val (getShiftUnit, _) = useProperty(props.dataLoader.lagUnit)
    val (getShiftedDateTime, _) = useProperty(props.dataLoader.masterDateTime)

    val (getPointDataFrame, setPointDataFrame) = useState(null as DataFrame<Branch, RowMajorDataFrame.Column<Branch, Any?>, Any?>?)
    useEffect(pointSelection, pathSelection, getShiftUnit, getShiftedDateTime.toRawString()) {
        if (pointSelection.size > 0 && pathSelection.size == 0) {
//                    val branchId = pathSelection[0].branch!!.branch.id
            val logicalBusId = pointSelection[0]

            val branchDataFrame = branchDataFrame(logicalBusId, getShiftedDateTime)

            setPointDataFrame(branchDataFrame)
        } else {
            setPointDataFrame(null)
        }
    }

    val (getFromDataFrame, setFromDataFrame) = useState(null as DataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?>?)
    val (getToDataFrame, setToDataFrame) = useState(null as DataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?>?)
    val (getPathDataFrame, setPathDataFrame) = useState(null as DataFrame<Branch, RowMajorDataFrame.Column<Branch, Any?>, Any?>?)
    useEffect(pathSelection, getShiftUnit, getMasterDateTime.toRawString()) {
        if (pathSelection.size > 0) {
//                    val branchId = pathSelection[0].branch!!.branch.id
            val logicalBranchId = pathSelection[0]
            val date = getMasterDateTime.toRawString()

            val (fromDataFrame, toDataFrame, branchDataFrame) = loadLogicalBranchDataFrames(logicalBranchId, date, getShiftUnit)

            setFromDataFrame(fromDataFrame)
            setToDataFrame(toDataFrame)
            setPathDataFrame(branchDataFrame)
        } else {
            setFromDataFrame(null)
            setToDataFrame(null)
            setPathDataFrame(null)
        }
    }

    val theme = useTheme<Theme>()
    val paletteType = if (theme.asDynamic().palette.mode == "dark") PaletteType.dark else PaletteType.light
    val (getLOD, _) = useProperty(props.dataLoader.lod)
    val (getDiagramType, _) = useProperty(props.dataLoader.diagramTypeProperty)

    useEffect(getLOD, getDiagramType) {
        val currentViewLoader = props.dataLoader.currentViewLoader
        if (currentViewLoader != null) {
            val graphical = currentViewLoader.graphicalNetwork.value
            if (graphical != null) {
                graphical.lodParent.innerHTML = HtmlSource("")

                if(getLOD) {
                    for (node in graphical.nodes) {
                        GlobalScope.launch {
                            val logicalBusId = node.id

                            val branchDataFrame = branchDataFrame(logicalBusId, getShiftedDateTime)

                            createChordDiagram(
                                branchDataFrame,
                                node.elements.first(),
                                graphical.lodParent,
                                paletteType
                            )
                        }
                    }
                }
            }
        }
    }

    when (EFluxGlobals.docking) {
        Docking.Overlay -> {
            MorphingMap {
                this.dataLoader = props.dataLoader
                this.viewLoader = props.diagramLoaders[0]
                this.show = props.diagramType == DiagramType.ThreeD
            }

            Schema {
                this.selection = props.dataLoader.schemaSelection
            }

            OverlayUI {
                WestFabBar {

                }

//                EastFabBar {
//                }

                SouthFabBar {

                }
            }

            if(showTable) {
                val customBackground: (SxProps<Theme>) -> Unit = { it ->
                    it.blurBackground(theme)
                }

                Paper {
                    sx = unsafeJso {
                        position = Position.absolute
                        top = 60.vh
                        bottom = (16.px + 40.px + 16.px)
                        left = 300.px
                        right = 16.px

                        customBackground(this)
                    }

                    TableView {
                        this.dataLoader = props.dataLoader
                        this.tableType = props.dataLoader.configuration.tableType
                        this.crossBorder = props.dataLoader.configuration.crossBorder
                        this.show = true
                        this.transparent = Color("rgba(255, 255, 255, 0.7)")
                    }
                }
            }

            UpdateIndicator {
                latestDateTimeProperty = props.dataLoader.configuration.latestDateTime
                maxDateTimeProperty = props.dataLoader.configuration.maxDateTime
                liveProperty = props.dataLoader.configuration.live
                anchorOrigin = unsafeJso<SnackbarOrigin> {
                    vertical = SnackbarOriginVertical.bottom
                    horizontal = SnackbarOriginHorizontal.center
                }
            }
        }

        Docking.Drawer -> {
            val (backgroundNodes, _) = useProperty(props.dataLoader.configuration.heatmapNodes)
            val (backgroundLinks, _) = useProperty(props.dataLoader.configuration.heatmapLinks)

            div {
                style = unsafeJso {
                    position = Position.absolute
                    top = EFluxGlobals.appBarHeight
                    left = 0.px
                    bottom = 0.px
                    right = 0.px
                }

                Diagram {
                    this.configuration = props.dataLoader.configuration
                    this.viewLoader = props.diagramLoaders[0]
                    this.show = props.diagramType == DiagramType.Tube
                    this.worldX = 0.0
                    this.worldY = 0.0
                    this.worldWidth = 686.0
                    this.worldHeight = 442.0

                    if(backgroundNodes == null) {
                        Heatmap {
                            this.configuration = props.dataLoader.configuration
                            this.viewLoader = props.diagramLoaders[0]
                            this.show = backgroundLinks != null
                            this.worldX = 0.0
                            this.worldY = 0.0
                            this.worldWidth = 686.0
                            this.worldHeight = 442.0
                        }
                    } else {
                        NodeHeatmap {
                            this.configuration = props.dataLoader.configuration
                            this.viewLoader = props.diagramLoaders[0]
                            this.show = backgroundLinks != null
                            this.worldX = 0.0
                            this.worldY = 0.0
                            this.worldWidth = 686.0
                            this.worldHeight = 442.0
                        }
                    }
                }

                Diagram {
                    this.configuration = props.dataLoader.configuration
                    this.viewLoader = props.diagramLoaders[2]
                    this.show = props.diagramType == DiagramType.Schematic
                    this.worldX = 0.0
                    this.worldY = 0.0
                    this.worldWidth = 686.4777968149511
                    this.worldHeight = 540.3881977097429

                    if(backgroundNodes == null) {
                        Heatmap {
                            this.configuration = props.dataLoader.configuration
                            this.viewLoader = props.diagramLoaders[2]
                            this.show = backgroundLinks != null
                            this.worldX = 0.0
                            this.worldY = 0.0
                            this.worldWidth = 686.4777968149511
                            this.worldHeight = 540.3881977097429
                        }
                    } else {
                        NodeHeatmap {
                            this.configuration = props.dataLoader.configuration
                            this.viewLoader = props.diagramLoaders[2]
                            this.show = backgroundLinks != null
                            this.worldX = 0.0
                            this.worldY = 0.0
                            this.worldWidth = 686.4777968149511
                            this.worldHeight = 540.3881977097429
                        }
                    }
                }

                Diagram {
                    this.configuration = props.dataLoader.configuration
                    this.viewLoader = props.diagramLoaders[1]
                    this.show = props.diagramType == DiagramType.Geo
                    this.worldX = 0.0
                    this.worldY = 0.0
                    this.worldWidth = 171.5
                    this.worldHeight = 113.82240085672915

                    if(backgroundNodes == null) {
                        Heatmap {
                            this.configuration = props.dataLoader.configuration
                            this.viewLoader = props.diagramLoaders[1]
                            this.show = backgroundLinks != null
                            this.worldX = 0.0
                            this.worldY = 0.0
                            this.worldWidth = 171.5
                            this.worldHeight = 113.82240085672915
                        }
                    } else {
                        NodeHeatmap {
                            this.configuration = props.dataLoader.configuration
                            this.viewLoader = props.diagramLoaders[1]
                            this.show = backgroundLinks != null
                            this.worldX = 0.0
                            this.worldY = 0.0
                            this.worldWidth = 171.5
                            this.worldHeight = 113.82240085672915
                        }
                    }
                }

//                ThreeD {
//                    this.dataLoader = props.dataLoader
//                    this.viewLoader = props.diagramLoaders[0]
//                    this.show = props.diagramType == DiagramType.ThreeD
//                    this.fps = 60.0
//                }
                MorphingMap {
                    this.dataLoader = props.dataLoader
                    this.viewLoader = props.diagramLoaders[0]
                    this.show = props.diagramType == DiagramType.ThreeD
                }

                println("showTable2 ${showTable}")
                if(showTable) {
                    val customBackground: (SxProps<Theme>) -> Unit = { it ->
                        it.blurBackground(theme)
                    }

                    Paper {
                        sx = unsafeJso {
                            position = Position.absolute
                            top = 400.px
                            bottom = 0.px
                            left = 0.px
                            right = 0.px

                            customBackground(this)
                        }

                        TableView {
                            this.dataLoader = props.dataLoader
                            this.show = true
                            this.transparent = Color("rgba(255, 255, 255, 0.7)")
                        }
                    }
                }
            }

            Sidebar() {
                this.uiscale = 1.0
            }

            Drawer {
                anchor = DrawerAnchor.right
                open = pointSelection.isNotEmpty() || pathSelection.isNotEmpty()
//                open = props.openDrawer
//                onClose = { _, _ -> props.onCloseMenu() }
                variant = DrawerVariant.persistent

                Container {
                    sx = unsafeJso {
                        this.width = 270.px
                        this.height = 100.pct
//                        top = EFluxGlobals.appBarHeight

                        // The width of the content will be taken into account and thus will fit the entire height (and not more)
                        display =  Display.contents
                        // Probably not taken into account if Display.contents
                        marginBottom = EFluxGlobals.appBarHeight
                    }

                    // Filler for the same height as the App bar
                    Toolbar {
                    }

                    IconButton {
                        onClick = {
                            props.dataLoader.configuration.logicalBusSelection.clearSelection()
                            props.dataLoader.configuration.logicalBranchSelection.clearSelection()
                        }

                        Close {
                        }
//                        ChevronRight {
//                        }
                    }

//                    props.MenuComponent?.let { +it }

                    if (getPathDataFrame != null && getFromDataFrame != null && getToDataFrame != null) {
                        val logicalNetwork = props.dataLoader.logicalNetwork.value
                        val pathSelection = logicalNetwork?.logicalLinks?.get(props.dataLoader.configuration.logicalBranchSelection.first())

                        SparklineDataFrameTable {
                            this.pathSelection = pathSelection?.branch
                            fromDataFrame = getFromDataFrame
                            toDataFrame = getToDataFrame
                            dataFrame = getPathDataFrame
                            dateTime = getShiftedDateTime.unixMillisLong
                        }
                    } else if (getPointDataFrame != null) {
                        val graphical = props.diagramLoaders[0].graphicalNetwork.value!!

                        if (pointSelection.isNotEmpty()) {
                            val logicalBusId = pointSelection.get(0)
                            val point = graphical.points.get(logicalBusId)!!

                            Typography {
                                +"Bus ${getPointDataFrame.rowCount}x${getPointDataFrame.columnCount}"
                            }
                            Chord {
                                dataFrame = getPointDataFrame
                                dateTime = getShiftedDateTime.unixMillisLong
                                originalShape = point.elements.first()
                            }
                        } else {
                            Typography {
                                +""
                            }
                        }
                    } else {
                        Typography {
                            +""
                        }
                    }
                }
            }
        }

        Docking.Docking -> {
            MorphingMap {
                this.dataLoader = props.dataLoader
                this.viewLoader = props.diagramLoaders[0]
                this.show = props.diagramType == DiagramType.ThreeD
            }

            Schema {
                this.selection = props.dataLoader.schemaSelection
            }

            div {
                css {
                    position = Position.absolute
                    top = 0.px
                    left = 0.px
                    right = 0.px
                    bottom = 0.px

                    pointerEvents = None.none
                }

                Fab {
                    css {
//                        position = Position.absolute
//                        top = 0.px
//                        left = 0.px
//                        right = 64.px
//                        bottom = 64.px
//                        height = 0.px
                        pointerEvents = PointerEvents.all
                    }

                    TerrainTwoTone {}

                    onClick = {
                        if (props.dataLoader.autoMorphing.value) {
                            props.dataLoader.autoMorphing.value = false
                            props.dataLoader.cartographic.value = 10.0
                        } else {
                            props.dataLoader.autoMorphing.value = true
                            props.dataLoader.cartographic.value = 0.0
                        }
                    }
                }
            }

            UpdateIndicator {
                latestDateTimeProperty = props.dataLoader.configuration.latestDateTime
                maxDateTimeProperty = props.dataLoader.configuration.maxDateTime
                liveProperty = props.dataLoader.configuration.live
            }

//            println("showTable2 ${showTable}")
//            if(showTable) {
//                val customBackground: (SxProps<Theme>) -> Unit = { it ->
//                    it.blurBackground(theme)
//                }
//
//                Paper {
//                    sx = unsafeJso {
//                        position = Position.absolute
//                        top = 400.px
//                        bottom = 0.px
//                        left = 0.px
//                        right = 0.px
//
//                        customBackground(this)
//                    }
//
//                    TableView {
//                        this.dataLoader = props.dataLoader.configuration
//                        this.show = true
//                        this.transparent = Color("rgba(255, 255, 255, 0.7)")
//                    }
//                }
//            }

            docking.nativeComponent.nativeComponent()
        }
    }

    val viewLoader = if (DiagramType.Schematic == props.dataLoader.diagramTypeProperty.value) props.dataLoader.diagramLoaders[2] else props.dataLoader.diagramLoaders[0]
    val (playback, _) = useProperty(props.dataLoader.playback)
    val (network, _) = useProperty(viewLoader.efluxNetwork)
    val (svgPanZoomContext, _) = useProperty(viewLoader.svgPanZoomContext)
    val (geoNetwork, _) = useProperty(props.dataLoader.geoNetwork)
    useMemo(playback, network, svgPanZoomContext, geoNetwork) {
//        println("Waiting for EFluxNetwork ${viewLoader.efluxNetwork.value}, ${viewLoader.svgPanZoomContext.value}")
        if(playback != null){
            if(playback.diagramType == DiagramType.ThreeD) {
                if(geoNetwork != null) {
                    playback.timeline.start()
                }
            } else {
                if(network != null && svgPanZoomContext != null) {
                    playback.timeline.start()
                }
            }
        }
    }
}

